import DOMPurify from 'dompurify';
import katex from 'katex';
import { marked } from 'marked';

/**
 * Renders a reply into HTML: LaTeX first (via KaTeX), then Markdown, then a sanitizer pass.
 *
 * The sanitizer is not optional. This HTML goes into `dangerouslySetInnerHTML`, which does no
 * sanitizing of its own, and the string it renders is model output shaped by tool results from a
 * catalog we do not control.
 */
export function renderMessage(text: string): string {
  const placeholders: { key: string; html: string }[] = [];
  let counter = 0;

  function protect(expr: string, displayMode: boolean): string {
    try {
      const html = katex.renderToString(expr.trim(), { displayMode });
      const key = `%%KATEX_${counter++}%%`;
      placeholders.push({ key, html });
      return key;
    } catch {
      return displayMode ? `<pre>${expr}</pre>` : `<code>${expr}</code>`;
    }
  }

  // Extracted before Markdown runs, so its escaping cannot mangle the expressions.
  let source = text;
  source = source.replace(/\$\$([\s\S]+?)\$\$/g, (_, expr: string) => protect(expr, true));
  source = source.replace(/\\\[([\s\S]+?)\\\]/g, (_, expr: string) => protect(expr, true));
  source = source.replace(/\$([^$\n]+?)\$/g, (_, expr: string) => protect(expr, false));
  source = source.replace(/\\\(([\s\S]+?)\\\)/g, (_, expr: string) => protect(expr, false));

  let html = marked.parse(source, { async: false });

  for (const { key, html: rendered } of placeholders) {
    html = html.replace(key, rendered);
  }

  // KaTeX emits MathML alongside its HTML, so both namespaces have to survive the sanitizer.
  return DOMPurify.sanitize(html, { USE_PROFILES: { html: true, mathMl: true, svg: true } });
}
